import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ASTERISK_API_BASE } from '../../config/bridge';
import {
  buildRecordingLookupFromCall,
  fetchRecordingAudioObjectUrl
} from '../../services/transcriptionService';

const PAGE_SIZE = 50;

const toDateInputValue = (date = new Date()) => {
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 10);
};

const createDefaultFilters = () => {
  const today = toDateInputValue();
  return {
    from: today,
    to: today,
    direction: '',
    agentId: '',
    callerNumber: ''
  };
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
};

const getDurationSeconds = (recording) => {
  const explicitDuration = Number(recording?.DurationSeconds ?? recording?.durationSeconds);
  if (Number.isFinite(explicitDuration) && explicitDuration > 0) return explicitDuration;

  const startedAt = recording?.RecordingStartedAt || recording?.recordingStartedAt ||
    recording?.CallStartedAt || recording?.callStartedAt;
  const stoppedAt = recording?.RecordingStoppedAt || recording?.recordingStoppedAt ||
    recording?.CallEndedAt || recording?.callEndedAt;
  const startTime = Date.parse(startedAt);
  const stopTime = Date.parse(stoppedAt);
  if (Number.isNaN(startTime) || Number.isNaN(stopTime) || stopTime < startTime) return 0;
  return Math.floor((stopTime - startTime) / 1000);
};

const formatDuration = (recording) => {
  const totalSeconds = getDurationSeconds(recording);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 1) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const readResponse = async (response) => {
  const text = await response.text();
  let payload = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = { error: text };
  }
  if (!response.ok) {
    throw new Error(payload?.error || payload?.message || `Request failed (${response.status})`);
  }
  return payload;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getRecordingKey = (recording) => String(
  recording?.RecordingKey ||
  recording?.RecordingName ||
  recording?.recordingName ||
  recording?.UniqueId ||
  recording?.uniqueId ||
  recording?.ChannelId ||
  recording?.channelId ||
  ''
).trim();

const buildRecordingQuery = (recording) => {
  const params = new URLSearchParams();
  Object.entries(buildRecordingLookupFromCall(recording)).forEach(([key, value]) => {
    const normalized = String(value || '').trim();
    if (normalized) params.set(key, normalized);
  });
  return params.toString();
};

const safeDownloadName = (recording) => String(
  recording?.RecordingName || recording?.recordingName || `call-recording-${Date.now()}.wav`
).replace(/[\\/:*?"<>|]/g, '_');

const BackofficeRecordingsPanel = ({ isActive }) => {
  const [filters, setFilters] = useState(createDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters);
  const [recordings, setRecordings] = useState([]);
  const [permissions, setPermissions] = useState({ view: false, download: false });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeRecording, setActiveRecording] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [audioLoadingId, setAudioLoadingId] = useState(null);
  const [audioError, setAudioError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const today = toDateInputValue();

  const clearAudioUrl = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = '';
    }
    setAudioUrl('');
  }, []);

  useEffect(() => () => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
  }, []);

  useEffect(() => {
    if (!audioUrl || !audioRef.current) return;
    audioRef.current.play().catch(() => {});
  }, [audioUrl]);

  useEffect(() => {
    if (!isActive) return undefined;
    const controller = new AbortController();
    const params = new URLSearchParams({
      from: appliedFilters.from,
      to: appliedFilters.to,
      page: String(page),
      pageSize: String(PAGE_SIZE)
    });
    ['direction', 'agentId', 'callerNumber'].forEach((key) => {
      const value = String(appliedFilters[key] || '').trim();
      if (value) params.set(key, value);
    });

    setLoading(true);
    setError('');
    fetch(`${ASTERISK_API_BASE}/backoffice/recordings?${params.toString()}`, {
      headers: { Accept: 'application/json', ...authHeaders() },
      signal: controller.signal
    })
      .then(readResponse)
      .then((payload) => {
        setRecordings(Array.isArray(payload?.recordings) ? payload.recordings : []);
        setPermissions({
          view: payload?.permissions?.view === true,
          download: payload?.permissions?.download === true
        });
        setTotal(Number(payload?.total || 0));
        setTotalPages(Math.max(1, Number(payload?.totalPages || 1)));
      })
      .catch((requestError) => {
        if (requestError?.name === 'AbortError') return;
        setRecordings([]);
        setTotal(0);
        setTotalPages(1);
        setError(requestError?.message || 'Failed to load call recordings.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [appliedFilters, isActive, page]);

  const resultLabel = useMemo(() => {
    if (!total) return '0 recordings';
    const first = ((page - 1) * PAGE_SIZE) + 1;
    const last = Math.min(total, page * PAGE_SIZE);
    return `${first}-${last} of ${total} recordings`;
  }, [page, total]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    const defaults = createDefaultFilters();
    setFilters(defaults);
    setPage(1);
    setAppliedFilters({ ...defaults });
  };

  const playRecording = async (recording) => {
    const recordingKey = getRecordingKey(recording);
    if (!recordingKey || audioLoadingId) return;
    clearAudioUrl();
    setAudioLoadingId(recordingKey);
    setAudioError('');
    try {
      const objectUrl = await fetchRecordingAudioObjectUrl(recording);
      audioUrlRef.current = objectUrl;
      setActiveRecording(recording);
      setAudioUrl(objectUrl);
    } catch (playError) {
      setAudioError(playError?.message || 'Recording could not be played.');
    } finally {
      setAudioLoadingId(null);
    }
  };

  const downloadRecording = async (recording) => {
    const recordingKey = getRecordingKey(recording);
    const query = buildRecordingQuery(recording);
    if (!recordingKey || !query || downloadingId || !permissions.download) return;
    setDownloadingId(recordingKey);
    setAudioError('');
    try {
      const response = await fetch(
        `${ASTERISK_API_BASE}/backoffice/recordings/download?${query}`,
        { headers: authHeaders() }
      );
      if (!response.ok) await readResponse(response);
      const blob = await response.blob();
      if (!blob.size) throw new Error('Recording audio is empty.');
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = safeDownloadName(recording);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (downloadError) {
      setAudioError(downloadError?.message || 'Recording could not be downloaded.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section className={`module-panel recordings-panel ${isActive ? 'active' : ''}`} aria-label="Call recordings">
      <div className="recordings-heading">
        <div>
          <h2>Call Recordings</h2>
          <p>Inbound and outbound call recordings</p>
        </div>
        <span className="recordings-result-count">{resultLabel}</span>
      </div>

      <div className="recordings-filter-bar">
        <label>
          <span>Date From</span>
          <input type="date" value={filters.from} max={today} onChange={(event) => updateFilter('from', event.target.value)} />
        </label>
        <label>
          <span>Date To</span>
          <input type="date" value={filters.to} max={today} onChange={(event) => updateFilter('to', event.target.value)} />
        </label>
        <div className="recordings-direction-filter">
          <span>Direction</span>
          <div className="recordings-segmented" role="group" aria-label="Call direction">
            {[
              ['', 'All'],
              ['inbound', 'Inbound'],
              ['outbound', 'Outbound']
            ].map(([value, label]) => (
              <button
                key={label}
                type="button"
                className={filters.direction === value ? 'active' : ''}
                onClick={() => updateFilter('direction', value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <label>
          <span>Agent / Extension</span>
          <input type="search" value={filters.agentId} onChange={(event) => updateFilter('agentId', event.target.value)} placeholder="e.g. 1015" />
        </label>
        <label>
          <span>Caller Number</span>
          <input type="search" value={filters.callerNumber} onChange={(event) => updateFilter('callerNumber', event.target.value)} placeholder="03XX..." />
        </label>
        <div className="recordings-filter-actions">
          <button type="button" className="btn btn-soft" onClick={resetFilters} disabled={loading}>Reset</button>
          <button type="button" className="btn btn-primary" onClick={applyFilters} disabled={loading}>
            {loading ? 'Loading...' : 'Apply Filter'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger recordings-alert" role="alert">{error}</div>}
      {audioError && <div className="alert alert-warning recordings-alert" role="alert">{audioError}</div>}

      {activeRecording && audioUrl && (
        <div className="recordings-player">
          <div className="recordings-player-copy">
            <strong>{activeRecording.RecordingName || 'Call recording'}</strong>
            <span>
              {activeRecording.CallerNumber || 'Unknown caller'} | {formatDateTime(activeRecording.RecordedAt)}
            </span>
          </div>
          <audio ref={audioRef} src={audioUrl} controls preload="metadata" />
        </div>
      )}

      <div className="recordings-table-wrap">
        <table className="recordings-table">
          <thead>
            <tr>
              <th>Date / Time</th>
              <th>Direction</th>
              <th>Agent</th>
              <th>Caller</th>
              <th>Duration</th>
              <th>Size</th>
              <th className="recordings-actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="recordings-empty">Loading recordings...</td></tr>
            )}
            {!loading && !error && recordings.length === 0 && (
              <tr><td colSpan="7" className="recordings-empty">No recordings found for the selected filters.</td></tr>
            )}
            {!loading && recordings.map((recording) => {
              const recordingKey = getRecordingKey(recording);
              const direction = String(recording.Direction || '').toLowerCase().startsWith('out') ? 'Outbound' : 'Inbound';
              const isAudioLoading = audioLoadingId === recordingKey;
              const isDownloading = downloadingId === recordingKey;
              return (
                <tr key={recordingKey} className={getRecordingKey(activeRecording) === recordingKey ? 'selected' : ''}>
                  <td>{formatDateTime(recording.RecordedAt)}</td>
                  <td><span className={`recordings-direction ${direction.toLowerCase()}`}>{direction}</span></td>
                  <td>{recording.AgentExtension || '-'}</td>
                  <td>
                    <strong>{recording.CallerNumber || '-'}</strong>
                    {recording.CallerName && <small>{recording.CallerName}</small>}
                  </td>
                  <td>{formatDuration(recording)}</td>
                  <td>{formatBytes(recording.FileSizeBytes)}</td>
                  <td>
                    <div className="recordings-row-actions">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => playRecording(recording)}
                        disabled={Boolean(audioLoadingId)}
                        aria-label={`Play ${recording.RecordingName || 'recording'}`}
                        title="Play recording"
                      >
                        <i className={`bi ${isAudioLoading ? 'bi-hourglass-split' : 'bi-play-fill'}`} aria-hidden="true"></i>
                      </button>
                      {permissions.download && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => downloadRecording(recording)}
                          disabled={Boolean(downloadingId)}
                          aria-label={`Download ${recording.RecordingName || 'recording'}`}
                          title="Download recording"
                        >
                          <i className={`bi ${isDownloading ? 'bi-hourglass-split' : 'bi-download'}`} aria-hidden="true"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="recordings-pagination">
        <span>Page {page} of {totalPages}</span>
        <div>
          <button
            type="button"
            className="btn btn-soft btn-sm"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={loading || page <= 1}
            aria-label="Previous page"
            title="Previous page"
          >
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            className="btn btn-soft btn-sm"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={loading || page >= totalPages}
            aria-label="Next page"
            title="Next page"
          >
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default BackofficeRecordingsPanel;
